import type { FC } from 'react'
import { memo, useCallback, useEffect, useState } from 'react'
import { format } from 'date-fns'
import Marker from '@/assets/images/playback-marker.svg'
import { usePlaybackPosition } from '@/hooks/usePlaybackPosition.ts'
import useTracksStore from '@/shared/store/tracks.store'
import useUiStore from '@/shared/store/ui.store.tsx'

interface TimelineBarProps {
  divisionsPerSecond: number
}

const TimelineBar: FC<TimelineBarProps> = ({ divisionsPerSecond }) => {
  const { position, handleClick, handleDrag, PLAYBACK_LEFT_PADDING } =
    usePlaybackPosition()
  const pixelsPerSecond = useUiStore((state) => state.pixelsPerSecond)
  const maxProjectDuration = useTracksStore((s) => s.maxProjectDuration)

  const [ticks, setTicks] = useState<number[]>([])
  const linesPerTick = 4
  const tickOffset = 12

  useEffect(() => {
    const totalLines = maxProjectDuration * divisionsPerSecond
    const totalTicks = Math.floor(totalLines / linesPerTick)
    setTicks(Array.from({ length: totalTicks }).map((_, i) => i))
  }, [divisionsPerSecond, maxProjectDuration])

  const formatTick = useCallback((seconds: number): string => {
    const date = new Date(seconds * 1000)
    return format(date, 'm:ss')
  }, [])

  const getTickTime = useCallback(
    (numTick: number): number => {
      const timePerTick = maxProjectDuration / ticks.length
      return numTick * timePerTick
    },
    [ticks, maxProjectDuration]
  )

  const tickPosition = useCallback(
    (numTick: number): number =>
      numTick * (pixelsPerSecond / divisionsPerSecond) * linesPerTick -
      tickOffset,
    [pixelsPerSecond, divisionsPerSecond]
  )

  const onMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    const startX = event.clientX
    const initialPosition = position

    const moveHandler = (moveEvent: MouseEvent) => {
      handleDrag(startX, initialPosition)(moveEvent.clientX)
    }

    const upHandler = () => {
      window.removeEventListener('mousemove', moveHandler)
      window.removeEventListener('mouseup', upHandler)
    }

    window.addEventListener('mousemove', moveHandler)
    window.addEventListener('mouseup', upHandler)
  }

  const onClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    handleClick(event.clientX, rect.left)
  }

  return (
    <div
      className="relative flex h-8 w-full flex-col justify-between bg-zinc-800"
      onClick={onClick}
    >
      <div
        className="absolute bottom-0 z-[49] -ml-[6.25px] h-3 w-[13.5px]"
        onMouseDown={onMouseDown}
        style={{
          left: position - PLAYBACK_LEFT_PADDING,
          cursor: 'grab',
        }}
      >
        <img src={Marker} className="h-3" draggable="false" />
      </div>

      <div className="relative w-full text-xs text-gray-400">
        {ticks.map((i) => (
          <span
            key={i}
            style={{ left: `${tickPosition(i)}px` }}
            className="absolute top-0.5 select-none font-semibold text-white"
          >
            {formatTick(getTickTime(i))}
          </span>
        ))}
        {ticks.length > 0 && (
          <span className="absolute -right-3 top-0.5 select-none font-semibold text-white">
            {formatTick(maxProjectDuration)}
          </span>
        )}
      </div>

      <div
        className="mt-1 h-2.5"
        style={{
          backgroundImage: `
            repeating-linear-gradient(
              to right,
              rgba(255, 255, 255, 0.1),
              rgba(255, 255, 255, 0.1) 1px,
              transparent 1px,
              transparent ${pixelsPerSecond / divisionsPerSecond}px
            )
          `,
        }}
      ></div>

      <div
        className="absolute bottom-0 left-0 z-10 h-2.5 w-full"
        style={{
          backgroundImage: `
            repeating-linear-gradient(
              to right,
              rgba(255, 255, 255, 0.5),
              rgba(255, 255, 255, 0.5) 1px,
              transparent 1px,
              transparent ${(pixelsPerSecond / divisionsPerSecond) * linesPerTick}px
            )
          `,
        }}
      ></div>
    </div>
  )
}

export default memo(TimelineBar)
